// RecurrencePicker — iPhone-alarm-style picker for one-time vs recurring
// schedules. Composes a small RRULE subset (ONCE / DAILY / WEEKLY / MONTHLY)
// plus an end condition (never / on date / after N occurrences). Days bit
// indexing matches Go time.Weekday: bit 0=Sun ... bit 6=Sat.

import type { ReminderEndType, ReminderFreq } from '../lib/api';

export type RecurrenceState = {
  freq: ReminderFreq;
  intervalN: number;
  daysMask?: number;
  monthlyDay?: number;
  startDate: string;       // YYYY-MM-DD
  endType: ReminderEndType;
  endDate?: string;
  endCount?: number;
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function RecurrencePicker({
  value,
  onChange,
}: {
  value: RecurrenceState;
  onChange: (next: RecurrenceState) => void;
}) {
  const setFreq = (freq: ReminderFreq) => {
    // Reset rule-specific fields when switching frequencies so a stale
    // days_mask doesn't leak into a Daily reminder, etc.
    const next: RecurrenceState = { ...value, freq };
    if (freq !== 'WEEKLY') next.daysMask = undefined;
    if (freq !== 'MONTHLY') next.monthlyDay = undefined;
    if (freq === 'WEEKLY' && (value.daysMask == null || value.daysMask === 0)) {
      // Default to the start date's weekday if no selection yet.
      const wd = weekdayOf(value.startDate);
      if (wd != null) next.daysMask = 1 << wd;
    }
    if (freq === 'ONCE') {
      next.intervalN = 1;
      next.endType = 'never';
      next.endDate = undefined;
      next.endCount = undefined;
    }
    onChange(next);
  };

  const toggleDay = (wd: number) => {
    const cur = value.daysMask ?? 0;
    const bit = 1 << wd;
    const next = cur ^ bit;
    onChange({ ...value, daysMask: next });
  };

  const setEndType = (t: ReminderEndType) => {
    const next: RecurrenceState = { ...value, endType: t };
    if (t !== 'on_date') next.endDate = undefined;
    if (t !== 'after_count') next.endCount = undefined;
    if (t === 'after_count' && next.endCount == null) next.endCount = 5;
    if (t === 'on_date' && !next.endDate) {
      // Default end_date to one month from start. Append T00:00:00 so the
      // YYYY-MM-DD parses as local midnight (without it, the spec parses
      // date-only strings as UTC midnight, which can shift the day-of-month
      // by one for users west of UTC).
      const d = new Date(value.startDate + 'T00:00:00');
      d.setMonth(d.getMonth() + 1);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      next.endDate = `${yyyy}-${mm}-${dd}`;
    }
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <Segmented
        label="Repeat"
        value={value.freq}
        options={[
          { value: 'ONCE', label: 'Once' },
          { value: 'DAILY', label: 'Daily' },
          { value: 'WEEKLY', label: 'Weekly' },
          { value: 'MONTHLY', label: 'Monthly' },
        ]}
        onChange={(v) => setFreq(v as ReminderFreq)}
      />

      {value.freq === 'DAILY' && (
        <Stepper
          label="Every N days"
          value={value.intervalN}
          min={1}
          max={365}
          onChange={(n) => onChange({ ...value, intervalN: n })}
        />
      )}

      {value.freq === 'WEEKLY' && (
        <div className="space-y-1.5">
          <span className="label-eyebrow">On these days</span>
          <div className="flex gap-1.5">
            {DAY_LABELS.map((d, i) => {
              const bit = 1 << i;
              const on = ((value.daysMask ?? 0) & bit) !== 0;
              return (
                <button
                  type="button"
                  key={d}
                  onClick={() => toggleDay(i)}
                  className={`flex-1 rounded-full border px-2 py-2.5 text-xs font-mono uppercase tracking-[0.1em] transition-colors ${
                    on
                      ? 'bg-accent text-accent-fg border-accent'
                      : 'bg-bg-sunken border-line text-fg-muted hover:text-fg'
                  }`}
                >
                  {d.slice(0, 1)}
                </button>
              );
            })}
          </div>
          <Stepper
            label="Every N weeks"
            value={value.intervalN}
            min={1}
            max={52}
            onChange={(n) => onChange({ ...value, intervalN: n })}
          />
        </div>
      )}

      {value.freq === 'MONTHLY' && (
        <div className="space-y-1.5">
          <Stepper
            label="On day of month"
            value={value.monthlyDay ?? new Date(value.startDate + 'T00:00:00').getDate()}
            min={1}
            max={31}
            onChange={(n) => onChange({ ...value, monthlyDay: n })}
          />
          <p className="text-xs text-fg-muted">Months without this day (e.g. Feb 31) are skipped.</p>
          <Stepper
            label="Every N months"
            value={value.intervalN}
            min={1}
            max={12}
            onChange={(n) => onChange({ ...value, intervalN: n })}
          />
        </div>
      )}

      <label className="block space-y-1.5">
        <span className="label-eyebrow">{value.freq === 'ONCE' ? 'On date' : 'Starting'}</span>
        <input
          type="date"
          value={value.startDate}
          onChange={(e) => onChange({ ...value, startDate: e.target.value })}
          className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm font-mono tabular-nums text-fg transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
        />
      </label>

      {value.freq !== 'ONCE' && (
        <div className="space-y-1.5">
          <Segmented
            label="End"
            value={value.endType}
            options={[
              { value: 'never', label: 'Never' },
              { value: 'on_date', label: 'On date' },
              { value: 'after_count', label: 'After N' },
            ]}
            onChange={(v) => setEndType(v as ReminderEndType)}
          />
          {value.endType === 'on_date' && (
            <input
              type="date"
              value={value.endDate ?? ''}
              onChange={(e) => onChange({ ...value, endDate: e.target.value })}
              className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm font-mono tabular-nums text-fg transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
            />
          )}
          {value.endType === 'after_count' && (
            <Stepper
              label="Number of occurrences"
              value={value.endCount ?? 5}
              min={1}
              max={365}
              onChange={(n) => onChange({ ...value, endCount: n })}
            />
          )}
        </div>
      )}
    </div>
  );
}

// summarizeRecurrence renders a one-line human description of a rule, used in
// the reminders list ("Daily at 09:00", "Mon Wed Fri at 14:30 · ends after 5",
// etc.). End conditions are appended so users can see the full intent at a
// glance.
export function summarizeRecurrence(r: RecurrenceState, hour: number, minute: number): string {
  const time = `${pad(hour)}:${pad(minute)}`;
  let base: string;
  switch (r.freq) {
    case 'ONCE':
      return `Once on ${r.startDate} at ${time}`;
    case 'DAILY':
      base = r.intervalN === 1 ? `Daily at ${time}` : `Every ${r.intervalN} days at ${time}`;
      break;
    case 'WEEKLY': {
      const days = describeDays(r.daysMask ?? 0);
      const prefix = r.intervalN === 1 ? '' : `Every ${r.intervalN} weeks · `;
      base = `${prefix}${days} at ${time}`;
      break;
    }
    case 'MONTHLY': {
      // Force local-midnight parsing so a UTC-west user doesn't see day N-1.
      const dom = r.monthlyDay ?? new Date(r.startDate + 'T00:00:00').getDate();
      base = r.intervalN === 1
        ? `Monthly on day ${dom} at ${time}`
        : `Every ${r.intervalN} months on day ${dom} at ${time}`;
      break;
    }
  }
  // ONCE returns above; for recurring schedules, append the end clause.
  if (r.endType === 'on_date' && r.endDate) {
    return `${base} · ends ${r.endDate}`;
  }
  if (r.endType === 'after_count' && r.endCount) {
    return `${base} · ends after ${r.endCount}`;
  }
  return base;
}

function describeDays(mask: number): string {
  if (mask === 0) return '(no days)';
  const all = mask === 0b1111111;
  if (all) return 'Every day';
  const weekday = mask === 0b0111110; // Mon-Fri
  const weekend = mask === 0b1000001; // Sun + Sat
  if (weekday) return 'Weekdays';
  if (weekend) return 'Weekends';
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    if ((mask & (1 << i)) !== 0) out.push(DAY_LABELS[i].slice(0, 3));
  }
  return out.join(' ');
}

function weekdayOf(yyyymmdd: string): number | null {
  const d = new Date(yyyymmdd + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  return d.getDay();
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

// --- internals ---

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="space-y-1.5">
      <span className="label-eyebrow">{label}</span>
      {/* Sunken track, ink pill for the live segment. Columns follow the option
          count so a 3-way strip doesn't leave a dead fourth cell. */}
      <div
        className="grid gap-1 rounded-full bg-bg-sunken p-1"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        {options.map((o) => {
          const on = o.value === value;
          return (
            <button
              type="button"
              key={o.value}
              onClick={() => onChange(o.value)}
              className={`rounded-full px-2 py-2.5 text-xs font-mono uppercase tracking-[0.1em] transition-colors ${
                on ? 'bg-accent text-accent-fg' : 'text-fg-muted hover:text-fg'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.round(n)));
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-fg">{label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= min}
          className="size-9 rounded-full border border-line-strong text-fg-muted hover:text-fg hover:bg-bg-sunken text-lg leading-none transition-colors disabled:opacity-40"
        >
          −
        </button>
        <input
          type="number"
          inputMode="numeric"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(clamp(Number(e.target.value)))}
          className="w-16 text-center rounded-control bg-bg-sunken border border-line px-2 py-2 text-sm font-mono tabular-nums text-fg transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
        />
        <button
          type="button"
          onClick={() => onChange(clamp(value + 1))}
          disabled={value >= max}
          className="size-9 rounded-full border border-line-strong text-fg-muted hover:text-fg hover:bg-bg-sunken text-lg leading-none transition-colors disabled:opacity-40"
        >
          +
        </button>
      </div>
    </div>
  );
}
